// src/components/eframes/EFrame223.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveHeader,
} from "../ui/ResponsiveLayout";
import BackToExecutorDashboardButton from "./BackToExecutorDashboardButton";

interface FinalInstructions {
  specialInstructions: string;
  importantContacts: string;
  documents: string;
  finalMessage: string;
  additionalWishes: string;
}

interface FuneralPreferences {
  serviceType: "traditional" | "memorial" | "celebration" | "none" | "";
  location: "funeral_home" | "religious" | "home" | "outdoor" | "other" | "";
  customLocation: string;
  burialType: "burial" | "cremation" | "donation" | "other" | "";
  burialTypeDetails: string;
  religiousService: boolean;
  religiousDetails: string;
  musicPreferences: string;
  readings: string;
  specialRequests: string;
}

interface MemorialDetails {
  musicPreferences: string;
  readings: string;
  speakers: string;
  flowers: string;
  donations: string;
  reception: boolean;
  receptionDetails: string;
  dress: string;
  personalTouches: string;
}

const EFrame223: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const planOwnerName = getPlanOwnerName(session.name);

  const [funeralPreferences, setFuneralPreferences] = useState<FuneralPreferences>({
    serviceType: "",
    location: "",
    customLocation: "",
    burialType: "",
    burialTypeDetails: "",
    religiousService: false,
    religiousDetails: "",
    musicPreferences: "",
    readings: "",
    specialRequests: "",
  });

  const [memorialDetails, setMemorialDetails] = useState<MemorialDetails>({
    musicPreferences: "",
    readings: "",
    speakers: "",
    flowers: "",
    donations: "",
    reception: false,
    receptionDetails: "",
    dress: "",
    personalTouches: "",
  });

  const [finalInstructions, setFinalInstructions] = useState<FinalInstructions>({
    specialInstructions: "",
    importantContacts: "",
    documents: "",
    finalMessage: "",
    additionalWishes: "",
  });

  useEffect(() => {
    const funeralAnswers = session?.taskProgress?.funeral?.answers;

    if (funeralAnswers?.funeralPreferences) {
      setFuneralPreferences(funeralAnswers.funeralPreferences);
    }

    if (funeralAnswers?.memorialDetails) {
      setMemorialDetails(funeralAnswers.memorialDetails);
    }

    if (funeralAnswers?.finalInstructions) {
      setFinalInstructions(funeralAnswers.finalInstructions);
    }
  }, [session?.taskProgress?.funeral]);

  const getServiceTypeLabel = (type: string) => {
    const labels = {
      traditional: "Traditional funeral service",
      memorial: "Memorial service (without body present)",
      celebration: "Celebration of life",
      none: "No formal service",
    };

    return labels[type as keyof typeof labels] || type;
  };

  const getLocationLabel = (location: string) => {
    const labels = {
      funeral_home: "Funeral home",
      religious: "Religious institution",
      home: "Family home",
      outdoor: "Outdoor location",
      other: "Other location",
    };

    return labels[location as keyof typeof labels] || location;
  };

  const getBurialTypeLabel = (type: string) => {
    const labels = {
      burial: "Traditional burial",
      cremation: "Cremation",
      donation: "Body donation to science",
      other: "Other/Alternative",
    };

    return labels[type as keyof typeof labels] || type;
  };

  const hasFinalInstructionsContent = useMemo(() => {
    return Boolean(
      finalInstructions.specialInstructions ||
        finalInstructions.importantContacts ||
        finalInstructions.documents ||
        finalInstructions.finalMessage ||
        finalInstructions.additionalWishes
    );
  }, [finalInstructions]);

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="mb-6 flex justify-start">
          <BackToExecutorDashboardButton />
        </div>
        <ResponsiveHeader title={`${planOwnerName}'s Funeral & Memorial Plan Summary`} />

        <div className="max-w-5xl">
          <div className="text-stone-800 text-lg font-medium font-sans mb-8">
            {planOwnerName} has outlined their funeral and memorial preferences here. 
            Familiarizing yourself with these plans now will help ensure their legacy is honored exactly as intended when the time comes. 
          </div>

          <div className="space-y-8">
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-stone-800">
                  Service Preferences
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-stone-600 mb-1">
                    Service Type
                  </dt>
                  <dd className="text-base text-stone-800">
                    {funeralPreferences.serviceType
                      ? getServiceTypeLabel(funeralPreferences.serviceType)
                      : "Not specified"}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-stone-600 mb-1">
                    Location
                  </dt>
                  <dd className="text-base text-stone-800">
                    {funeralPreferences.location
                      ? getLocationLabel(funeralPreferences.location)
                      : "Not specified"}
                    {funeralPreferences.customLocation &&
                      ` - ${funeralPreferences.customLocation}`}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-stone-600 mb-1">
                    Remains
                  </dt>
                  <dd className="text-base text-stone-800">
                    {funeralPreferences.burialType
                      ? getBurialTypeLabel(funeralPreferences.burialType)
                      : "Not specified"}
                  </dd>
                </div>

                {funeralPreferences.burialType === "other" &&
                  funeralPreferences.burialTypeDetails && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">
                        Remains Details
                      </dt>
                      <dd className="text-base text-stone-800">
                        {funeralPreferences.burialTypeDetails}
                      </dd>
                    </div>
                  )}

                <div>
                  <dt className="text-sm font-medium text-stone-600 mb-1">
                    Religious Service
                  </dt>
                  <dd className="text-base text-stone-800">
                    {funeralPreferences.religiousService ? "Yes" : "No"}
                  </dd>
                </div>

                {funeralPreferences.religiousDetails && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-stone-600 mb-1">
                      Religious Details
                    </dt>
                    <dd className="text-base text-stone-800">
                      {funeralPreferences.religiousDetails}
                    </dd>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-stone-800">
                  Memorial & Celebration Details
                </h3>
              </div>

              <div className="space-y-4">
                {memorialDetails.musicPreferences && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">
                      Music Preferences
                    </dt>
                    <dd className="text-base text-stone-800">
                      {memorialDetails.musicPreferences}
                    </dd>
                  </div>
                )}

                {memorialDetails.readings && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">
                      Readings or Poems
                    </dt>
                    <dd className="text-base text-stone-800">
                      {memorialDetails.readings}
                    </dd>
                  </div>
                )}

                {memorialDetails.speakers && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">
                      Speakers
                    </dt>
                    <dd className="text-base text-stone-800">
                      {memorialDetails.speakers}
                    </dd>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {memorialDetails.flowers && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">
                        Flower Preferences
                      </dt>
                      <dd className="text-base text-stone-800">
                        {memorialDetails.flowers}
                      </dd>
                    </div>
                  )}

                  {memorialDetails.donations && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">
                        Charitable Donations
                      </dt>
                      <dd className="text-base text-stone-800">
                        {memorialDetails.donations}
                      </dd>
                    </div>
                  )}

                  {memorialDetails.dress && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">
                        Dress Code
                      </dt>
                      <dd className="text-base text-stone-800">
                        {memorialDetails.dress}
                      </dd>
                    </div>
                  )}

                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">
                      Reception
                    </dt>
                    <dd className="text-base text-stone-800">
                      {memorialDetails.reception ? "Yes" : "No"}
                    </dd>
                  </div>
                </div>

                {memorialDetails.receptionDetails && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">
                      Reception Details
                    </dt>
                    <dd className="text-base text-stone-800">
                      {memorialDetails.receptionDetails}
                    </dd>
                  </div>
                )}

                {memorialDetails.personalTouches && (
                  <div>
                    <dt className="text-sm font-medium text-stone-600 mb-1">
                      Personal Touches
                    </dt>
                    <dd className="text-base text-stone-800">
                      {memorialDetails.personalTouches}
                    </dd>
                  </div>
                )}
              </div>
            </div>

            {hasFinalInstructionsContent && (
              <div className="bg-white border border-neutral-200 rounded-lg p-6">
                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-stone-800">
                    Final Instructions & Messages
                  </h3>
                </div>

                <div className="space-y-4">
                  {finalInstructions.specialInstructions && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">
                        Special Instructions
                      </dt>
                      <dd className="text-base text-stone-800 whitespace-pre-wrap">
                        {finalInstructions.specialInstructions}
                      </dd>
                    </div>
                  )}

                  {finalInstructions.importantContacts && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">
                        Important Contacts
                      </dt>
                      <dd className="text-base text-stone-800 whitespace-pre-wrap">
                        {finalInstructions.importantContacts}
                      </dd>
                    </div>
                  )}

                  {finalInstructions.documents && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">
                        Documents
                      </dt>
                      <dd className="text-base text-stone-800 whitespace-pre-wrap">
                        {finalInstructions.documents}
                      </dd>
                    </div>
                  )}

                  {finalInstructions.finalMessage && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">
                        Final Message
                      </dt>
                      <dd className="text-base text-stone-800 whitespace-pre-wrap">
                        {finalInstructions.finalMessage}
                      </dd>
                    </div>
                  )}

                  {finalInstructions.additionalWishes && (
                    <div>
                      <dt className="text-sm font-medium text-stone-600 mb-1">
                        Additional Wishes
                      </dt>
                      <dd className="text-base text-stone-800 whitespace-pre-wrap">
                        {finalInstructions.additionalWishes}
                      </dd>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame223;